import ActionButton from 'components/ActionButton';
import AddSupplierOnUserModal from 'components/AddSupplierOnUserModal';
import DeleteActionForTable from 'components/DeleteActionForTableData';
import { Loading } from 'components/loading/Loading';
import CustomLink from 'components/TableLink';
import { tableStyles } from 'css/tableStyles';
import React, { useEffect, useMemo, useState } from 'react';
import DataTable from 'components/DataTable';
import { getPageLimit, showError, showSuccess, updatePageLimit } from 'utils';
import styles from './index.module.scss';

const filters: { [p: string]: any; page: number } = {
  page: 0,
};

const SuppliersTable = props => {
  const {
    userId,
    entityName,
    addSupplierAPI,
    deleteSupplierAPI,
    fecthSuppliersAPI,
  } = props;
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<Record<string, any>>({});
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const fetchSuppliersFn = async () => {
    try {
      const response = await fecthSuppliersAPI({
        ...filters,
        userId,
        limit: getPageLimit(),
      });
      setData(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliersFn();
  }, []);

  const deleteSupplierFn = async (supplierId: string) => {
    try {
      await deleteSupplierAPI(userId, supplierId);
      await fetchSuppliersFn();
      showSuccess('Supplier removed successfully.');
    } catch (e) {
      showError(e);
    }
  };

  const columns = useMemo(() => [
    {
      name: 'Supplier ID',
      selector: 'objectId',
      sortable: false,
      // eslint-disable-next-line react/display-name
      cell: row => (
        <CustomLink
          text={row.objectId}
          linkTo={`/suppliers/${row.objectId}`}
        />
      ),
    },
    {
      name: 'Name',
      selector: 'name',
      sortable: true,
    },
    {
      name: 'Email',
      selector: 'email',
      sortable: true,
    },
    {
      name: 'Phone',
      selector: 'phone',
      sortable: true,
    },
    {
      name: 'Action',
      selector: '',
      sortable: true,
      // eslint-disable-next-line react/display-name
      cell: (row: Record<string, any>) => (
        <div className={'ml-3'}>
          <DeleteActionForTable
            onClick={() => deleteSupplierFn(row.objectId)}
          />
        </div>
      ),
    },
  ], []);

  const onChangePage = (page: number) => {
    filters.page = page - 1;
    fetchSuppliersFn();
  };

  const onChangeRowsPerPage = (rowsPerPage: number, currentPage: number) => {
    filters.limit = rowsPerPage;
    updatePageLimit(rowsPerPage);
    onChangePage(currentPage);
  };

  if (loading) return (
    <div className={styles['loader-container']}>
      <Loading type={'mini'} />
    </div>
  );
  
  return (
    <div className={styles['users-container']}>
      {modalOpen && (
        <AddSupplierOnUserModal
          userId={userId}
          addSupplierAPI={addSupplierAPI}
          onSuccess={fetchSuppliersFn}
          toggleModal={() => setModalOpen(!modalOpen)}
        />
      )}
      <div className={styles['section-header']}>
        <div className={styles['section-heading']}>{`${entityName} Suppliers`}</div>
        <ActionButton
          active={true}
          isLoading={false}
          onClick={() => setModalOpen(true)}
          text={<>Supplier <span className={'ml-3'}>+</span></>}
        />
      </div>
      <DataTable
        columns={columns}
        noHeader={true}
        data={data.items}
        highlightOnHover={true}
        defaultSortField="createdAt"
        customStyles={tableStyles}
        keyField="objectId"
        pagination={true}
        paginationServer={true}
        paginationPerPage={getPageLimit()}
        paginationDefaultPage={(filters.page || 0) + 1}
        paginationTotalRows={data.total}
        onChangePage={onChangePage}
        onChangeRowsPerPage={onChangeRowsPerPage}
      />
    </div>
  );
};

export default SuppliersTable;